// src/services/securityAlert.js
//
// 🟢 THE BROWSER'S COPY OF "A NEW DEVICE JUST SIGNED IN".
//
// The phone writes this record whenever it approves a login (services/SecurityAlerts.ts
// on the app side). There is exactly one route where the phone never sees the approval:
// this browser letting ITSELF in with a code emailed to the account. Nobody else can
// write it, so the browser does — same chat, same message type, same JSON payload, so
// the app's SecurityAlertCard renders it without knowing where it came from.
//
// 🟥 SEALED, like everything else. Note to Self is a 1:1 chat with yourself, so sealing
//    to `[uid]` produces one copy per device YOU own — including this browser and every
//    phone — and nothing else can read it.

import { collection, doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { sealForUsers } from '../crypto/e2ee';
import { getSocket } from './socket';
import { deviceLabel, browserName, resolveLocation } from './session';

/** Mirrors the LoginAlert type in the app's services/SecurityAlerts.ts — keep in step. */
export async function recordLoginAlert(uid, { method = 'email_code', location } = {}) {
  if (!uid) return false;

  const loc = location || (await resolveLocation());
  const payload = {
    kind: 'login',
    deviceName: deviceLabel(),
    platform: 'web',
    browser: browserName(),
    city: loc?.city || '',
    country: loc?.country || '',
    region: '',
    latitude: Number(loc?.latitude) || 0,
    longitude: Number(loc?.longitude) || 0,
    method,
    at: Date.now(),
  };

  const chatId = `${uid}_${uid}`;
  const plain = JSON.stringify(payload);

  let cloudText = plain;
  let perDeviceKeys = null;
  let senderDeviceId = null;
  try {
    const sealed = await sealForUsers(plain, [uid]);
    if (sealed?.perDeviceKeys && Object.keys(sealed.perDeviceKeys).length > 0) {
      cloudText = '🔒 [Encrypted Message]';
      perDeviceKeys = sealed.perDeviceKeys;
      senderDeviceId = sealed.senderDeviceId;
    }
  } catch {
    // sealForUsers is fail-closed by design (see crypto/e2ee.js). An unsealed alert would
    // put a device name and a city in the clear, so we'd rather write nothing.
    return false;
  }
  if (!perDeviceKeys) return false;

  const ref = doc(collection(db, 'chats', chatId, 'messages'));
  try {
    await setDoc(ref, {
      type: 'security',
      text: cloudText,
      senderId: uid,
      timestamp: serverTimestamp(),
      sent: true,
      delivered: true,
      seen: false,
      perDeviceKeys,
      senderDeviceId,
    });
  } catch {
    return false;
  }

  try {
    await setDoc(doc(db, 'chats', chatId), {
      participants: [uid, uid],
      lastMessage: cloudText,
      timestamp: serverTimestamp(),
      lastMessageSenderId: uid,
      lastMessageId: ref.id,
      lastMessageSent: true,
      lastMessageDelivered: true,
      lastMessageSeen: false,
    }, { merge: true });
  } catch { /* preview only */ }

  // 🟥 THE PUSH CARRIES NO CIPHER, ON PURPOSE. The server drops `message` straight into
  //    the notification body, and when a cipher is supplied the phones' native handlers
  //    decrypt it and show the result — which here is the JSON payload, coordinates
  //    included, on a lock screen. One safe sentence goes over the wire; the real card is
  //    read from the sealed document above.
  try {
    const s = getSocket();
    if (s) {
      s.emit('new-message', {
        toUserId: uid,
        fromUserId: uid,
        chatId,
        messageId: String(ref.id),
        clientMsgId: String(ref.id),
        message: 'New login detected',
        nonce: null,
        type: 'security',
        isGroup: false,
      });
    }
  } catch { /* the record is written either way */ }

  return true;
}
